import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, BarChart3 } from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";
import Breadcrumb from "../components/Breadcrumb";
import { buildBenchmarkCombos } from "../lib/industryConceptMap";
import {
  setStandardMeta,
  upsertJsonLd,
  buildOrganizationSchema,
  buildBreadcrumbSchema,
  ORG,
} from "../lib/seo";

/**
 * /benchmarks — hub page listing every (industry × concept) benchmark page.
 *
 * Grouped by industry so each block is scannable and each internal link is
 * one hop from a section header (good for crawl depth and topical clustering).
 */
export default function BenchmarksIndexPage() {
  const combos = useMemo(() => buildBenchmarkCombos(), []);
  const byIndustry = useMemo(() => {
    const map = new Map();
    for (const c of combos) {
      if (!map.has(c.industrySlug)) map.set(c.industrySlug, []);
      map.get(c.industrySlug).push(c);
    }
    return map;
  }, [combos]);

  useEffect(() => {
    const url = `${ORG.url}/benchmarks`;
    setStandardMeta({
      title:
        "Industry Financial Benchmarks — Real Peer Data by Sector | CFO Grade",
      description:
        "Free financial benchmarks by industry. See the healthy range and real peer medians for margins, DSCR, cash cycle, and 20+ other ratios across restaurants, SaaS, e-commerce, construction, and 7 more sectors.",
      url,
    });
    upsertJsonLd("benchmarks-index-graph", {
      "@context": "https://schema.org",
      "@graph": [
        buildOrganizationSchema(),
        buildBreadcrumbSchema([
          { name: "Home", url: ORG.url },
          { name: "Benchmarks", url },
        ]),
        {
          "@type": "CollectionPage",
          "@id": `${url}#collection`,
          url,
          name: "CFO Grade Industry Benchmarks",
          description:
            "Every industry-specific financial benchmark computed by CFO Grade, grouped by sector.",
          isPartOf: { "@id": `${ORG.url}#website` },
          hasPart: combos.map((c) => ({
            "@type": "WebPage",
            url: `${ORG.url}/benchmarks/${c.industrySlug}/${c.conceptSlug}`,
            name: `${c.concept.title} · ${c.industry.sub}`,
          })),
        },
      ],
    });
    return () => {
      const el = document.getElementById("benchmarks-index-graph");
      if (el) el.remove();
    };
  }, [combos]);

  return (
    <div
      className="min-h-screen bg-[#F7F5F0] bg-grain"
      data-testid="benchmarks-index-page"
    >
      <header className="max-w-4xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandWordmark size="sm" />
        </Link>
        <Link
          to="/"
          className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to home
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-10 pt-6 pb-20">
        <Breadcrumb
          items={[
            { name: "Home", to: "/" },
            { name: "Benchmarks", to: "/benchmarks" },
          ]}
        />

        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C665D] bg-white border border-[#E5E0D8] rounded-full px-3 py-1.5 mt-4">
          <BarChart3 size={13} strokeWidth={1.5} /> Benchmarks by industry
        </div>

        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F382E] leading-[1.05] tracking-tight mt-5">
          What&apos;s healthy —
          <span className="italic text-[#C26D5C]"> for your industry.</span>
        </h1>
        <p className="text-base sm:text-lg text-[#1A1A1A] leading-relaxed mt-5 max-w-2xl">
          Every industry has its own definition of &ldquo;healthy.&rdquo; A 15% EBITDA
          margin is stellar for a grocery store and mediocre for a SaaS. Pick
          your industry, then pick the metric.
        </p>

        <div className="mt-10 space-y-10">
          {[...byIndustry.entries()].map(([industrySlug, list]) => {
            const industry = list[0].industry;
            return (
              <section
                key={industrySlug}
                data-testid={`benchmarks-industry-${industrySlug}`}
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">
                    {industry.sub}
                  </h2>
                  <Link
                    to={`/for/${industrySlug}`}
                    className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1 transition-colors"
                  >
                    Industry hub{" "}
                    <ArrowRight size={12} strokeWidth={1.5} />
                  </Link>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {list.map((c) => (
                    <Link
                      key={c.conceptSlug}
                      to={`/benchmarks/${industrySlug}/${c.conceptSlug}`}
                      className="group flex items-center justify-between bg-white border border-[#E5E0D8] hover:border-[#1F382E]/30 rounded-xl px-4 py-3 transition-all"
                      data-testid={`benchmarks-link-${industrySlug}-${c.conceptSlug}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-[#1F382E] group-hover:text-[#C26D5C] transition-colors">
                          {c.concept.title}
                        </p>
                        <p className="text-[12px] text-[#5C665D] mt-0.5">
                          Target: {c.industryTarget.value}
                        </p>
                      </div>
                      <ArrowRight
                        size={14}
                        strokeWidth={1.5}
                        className="text-[#5C665D] group-hover:translate-x-0.5 transition-transform shrink-0"
                      />
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <section className="mt-14 bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-8">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#C26D5C]">
            Or skip ahead
          </p>
          <p className="font-serif-display text-2xl text-[#1F382E] tracking-tight mt-2">
            Run a free CFO Grade in 5 minutes.
          </p>
          <p className="text-sm text-[#5C665D] mt-2 max-w-md">
            Paste your P&L and see how you stack up against every one of
            these benchmarks — plus a plain-English memo of what to fix first.
          </p>
          <Link
            to="/analyze?utm_source=benchmarks_index"
            className="inline-flex items-center gap-2 mt-5 bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-6 text-sm font-medium transition-all duration-300"
            data-testid="benchmarks-index-cta"
          >
            Analyze my business{" "}
            <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </section>
      </main>

      <SiteFootnote />
    </div>
  );
}

void BrandMark;
